import React, { useContext, useEffect, useState } from 'react'
import SelectFacet from '../../SearchFacets/SelectFacet'
import ResultPreview from './Results/ResultPreview/ResultPreview'
import './ContentType.scss'
import { ContentSearchBar } from './ContentSearchBar/ContentSearchBar'
import { KeyboardBackspace } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { constants } from '../../../lib/constants'
import ResultModal from './Results/ResultModal/ResultModal'
import itemImage from '../../../images/findingDef.png'
import SideNavButtonFindings from './SideNavigation/SideNavButton/SideNavButtonFindings'
import { obtainGroups } from './CTHelpers/GroupHelper'
import { UserContext } from '../../context/UserContext'
import { checkAccess, queryContent } from './CTHelpers/CheckAccess'
import { CTErrorPage, makeNoAccessRows } from './CTHelpers/CTErrorPage'
import Loader from '../../PageLoader'
import ContentManageButton from '../contentManageButton'
import EmbedShoppingCart from '../../ShoppingCart/embedShoppingCart'

// Export the Findings details data.
export const Findings = ({ ids }) => {
  const CTName = 'Finding'
  // Set all required states.
  // Set filter data.
  const [filter, setFilter] = useState({
    group: constants.filter.all,
    text: ''
  })
  const history = useNavigate()
  // Set group data.
  const [groups, setGroups] = useState()
  // Set data to be displayed.
  const [dataToDisplay, setDataToDisplay] = useState()
  // Set all data.
  const [allData, setAllData] = useState()
  // Set site preview data.
  const [sidePreviews, setSidePreviews] = useState()
  // Set if modal need to be shown.
  const [shouldShowModal, setShouldShowModal] = useState(false)
  // Set the selected item.
  const [selectedItem, setSelectedItem] = useState({})
  // Set loading state.
  const [isLoading, setIsLoading] = useState(true)
  // Set if have availability error.
  const [notAvailable, setNotAvailable] = useState()
  // Set if have loading error.
  const [hasLoadingError, setHasLoadingError] = useState(false)
  // Set if have null value error.
  const [hasNullValue, setHasNullValue] = useState(false)
  // Set if has access or not.
  const [onlyNoAccess, setOnlyNoAccess] = useState(false)
  // Fetch the user context.
  const { userRoles, userName, otherDetails } = useContext(UserContext)

  // Check node access when ids are available.
  useEffect(() => {
    let uniqueIds = []
    // Filter invalid ids
    if (ids) {
      uniqueIds = [...new Set(ids.split(','))].filter(Number)
    }
    // Check for Null State
    if (!ids || uniqueIds.length === 0) {
      setHasNullValue(true)
      setIsLoading(false)
    } else {
      // Check for Access
      const promises = uniqueIds.map(i => checkAccess(i))
      const show = []
      const hide = []
      Promise.all(promises).then(accessObjs => {
        accessObjs.forEach(obj => {
          // Show data if have access
          if (obj.access) {
            show.push(obj.item)
          } else {
            // Hide data or generate error base on code received
            if (obj.error === 403) {
              hide.push(obj.item)
            }
            if (obj.error === 404) {
              setHasLoadingError(true)
              setIsLoading(false)
            }
          }
        })
      }).then(() => {
        // Generate proper user information.
        const userInfo = userName || otherDetails.name
        // If user have access.
        if (show && show.length > 0) {
          const findingVariables = JSON.stringify({
            ids: show
          })
          // Build the query URL string & call to fetch data.
          const findingQuery = `${process.env.REACT_APP_TARGET_URL}/graphql?queryId=${constants.drupalQueryIds.findingsQuery}&variables=${findingVariables}`
          queryContent(findingQuery)
            .then(response => {
              if (!response?.data?.nodeQuery?.count) {
                setHasLoadingError(true)
              } else {
                const entities = response?.data?.nodeQuery?.entities
                setAllData(entities)
                setDataToDisplay(entities)
              }
            })
            .catch(() => setHasLoadingError(true))
            .finally(() => setIsLoading(false))
          // Set different states based on retrieve data.
          const groupsData = obtainGroups(show)
          setGroups(groupsData)
          // Hide the necessary data.
          if (hide && hide.length > 0) {
            // Fetch no access html.
            const noAccessRows = makeNoAccessRows(CTName, hide, userInfo)
            setNotAvailable(noAccessRows)
          }
        } else {
          if (!hide || hide.length === 0) {
            setHasLoadingError(true)
            setIsLoading(false)
          } else {
            // Fetch no access html.
            const noAccessRows = makeNoAccessRows(CTName, hide, userInfo)
            setOnlyNoAccess(true)
            setNotAvailable(noAccessRows)
            setIsLoading(false)
          }
        }
      })
    }
  }, [ids, userName, otherDetails.name])

  // Filter the data based on allData and filter.
  useEffect(() => {
    // Filter for groups.
    switch (filter.group) {
      case constants.filter.all:
        setDataToDisplay(allData)
        break
      case constants.filter.none:
        setDataToDisplay(allData.filter(item => !item.group))
        break
      default:
        setDataToDisplay(allData.filter(item => item.group === filter.group))
        break
    }
    // Filter for text.
    if (filter.text.length > 0) {
      setDataToDisplay(data => (data.filter(item => JSON.stringify(item).toLowerCase().indexOf(filter.text.toLowerCase()) > -1)))
    }
  }, [allData, filter])

  // Filter all the types & data.
  const filterType1 = [
    constants.filter.all,
    constants.filter.none,
    ...(new Set(allData?.reduce((acc, entity) => {
      if (entity.group) {
        acc.push(entity.group)
      }
      return acc
    }, [])))
  ]

  const filterData = category => value => {
    setFilter(data => ({
      ...data,
      [category]: value
    }))
  }

  // Create pages for modal.
  const createPages = item => {
    return [
      {
        topics: [
          {
            title: 'Base Information',
            fields: [
              { title: 'Audit ID', input: item.findingAuditID?.entity?.title },
              { title: 'Classification', input: item.findingClassification?.entity?.title },
              { title: 'Clinical E2E Core Category', input: item.findingClinE2ECat?.entity?.title },
              { title: 'Compliance Topic', input: `${item.findingComplianceTopic?.entity?.title} - ${item.findingComplianceSubTopic?.entity?.title}` },
              { title: 'Department', input: `${item.findingDepartment?.entity?.title} - ${item.findingSubdepartment?.entity?.title}` },
              { title: 'Quality Process', input: item.findingQualityProcess?.entity?.title },
              { title: 'Quality Process Group', input: item.findingQualityProcessGroup?.entity?.title },
              { title: 'Group', input: item.groups.join(', ') },
              { title: 'Source System', input: item.findingSourceSystem?.entity?.title },
              { title: 'Type', input: item?.findingTitle }
            ]
          }
        ]
      },
      {
        topics: [
          {
            title: 'Detailed Information',
            fields: [
              { title: 'Description', input: item.findingDescription?.value != null ? <div dangerouslySetInnerHTML={{ __html: item.findingDescription?.value }} /> : constants.filter.none },
              { title: 'Details', input: item.findingDetails?.value != null ? <div dangerouslySetInnerHTML={{ __html: item.findingDetails?.value }} /> : constants.filter.none }
            ]
          }
        ]
      }
    ]
  }

  // Create preview data for multiple findings.
  const createPreviews = data => {
    // Loop through all finding data.
    data.forEach(finding => {
      const currentGroups = []
      // Loop through the group.
      groups.forEach(group => {
        if (group.id === finding.nid) {
          currentGroups.push(group.group)
        }
      })
      finding.groups = currentGroups
    })
    // Return data for preview.
    // Display manage button based on user role.
    return data && data.map((item, i) =>
      <div key={i} className='result-row'>
        <EmbedShoppingCart item={item} image={itemImage} bundle="findings" />
        <ResultPreview
          title={item.title}
          nid={item.nid}
          naturalKey={item.naturalKey}
          group={item.groups}
          itemImage={itemImage}
          openModalCallback={() => {
            setSelectedItem(item)
            setShouldShowModal(true)
          }}
        />
        <div className='button-wrappers'>
          <SideNavButtonFindings
            setSidePreviews={setSidePreviews}
            audit={item.findingAuditID?.entity}
            capas={item.CAPAs?.entities}
            qis={item.QIs?.entities}
          />
          {
            userRoles.administrator ||
              userRoles.contentAuthor ||
              userRoles.feedEngineer ||
              userRoles.groupAdmin
              ? <ContentManageButton nid={item.nid} />
              : <div />
          }
        </div>
      </div>
    )
  }

  // Display error message if null value received.
  if (hasNullValue && !isLoading) {
    const message = <Typography dangerouslySetInnerHTML={{ __html: constants.errors.nullValue }} align='center' />
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}s`} message={message} />
    )
  }

  // Display error message if don't have access.
  if (onlyNoAccess && !isLoading && notAvailable) {
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}s`} message={notAvailable} />
    )
  }

  // Display error message if there is some loading error.
  if (hasLoadingError && !isLoading) {
    const message = <Typography dangerouslySetInnerHTML={{ __html: constants.errors.invalidContent }} align='center' />
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}s`} message={message} />
    )
  }

  // Show loader if data fetching in progress.
  if (isLoading) {
    // Return the loader.
    return <Loader />
  }

  // Return entire data for display purpose.
  return !isLoading && allData && (
    <div className='content-wrapper'>
      <div className='header-wrapper'>
        <div className='back-button__clickable' onClick={() => { history(-1) }}>
          <KeyboardBackspace /> <Typography>Back</Typography>
        </div>
        <div className='content-title'>
          <Typography variant='h3'>
            Findings
          </Typography>
        </div>
      </div>
      <div className='search-bar-wrapper'>
        <ContentSearchBar
          setFilter={value => setFilter(data => ({
            ...data,
            text: value
          }))}
        />
      </div>
      <div className='result-wrapper'>
        <div className='filter-wrapper--left-column'>
          <SelectFacet
            onChange={value => {
              filterData('group')(value)
            }}
            selectOptions={filterType1}
            title='Group'
          />
        </div>
        <div className='table-wrapper'>
          {createPreviews(dataToDisplay)}
          {notAvailable}
        </div>
        <div className='sidebar-wrapper--right-column'>
          {sidePreviews}
        </div>
      </div>
      {Object.keys(selectedItem).length > 0 &&
        <ResultModal
          isOpen={shouldShowModal}
          onClose={() => setShouldShowModal(false)}
          nid={selectedItem.nid}
          naturalKey={selectedItem.naturalKey}
          title={selectedItem.title}
          pages={createPages(selectedItem)}
        />}
    </div>
  )
}
